import { useNavigate } from 'react-router-dom';
import type { NurseryProduct } from '../../types/nursery';
import { toNum } from '@/utils/numeric';
import { TagSwatch } from './TagSwatch';

export interface ProductCardProps {
  product: NurseryProduct;
  /** Nursery admins open the editor; everyone else gets a plain card. */
  editable?: boolean;
  /** The crop it is listed under: its stock items', as a product has none of its own. */
  crop?: string | null;
}

/**
 * An umbrella product: the name customers buy, and the stock items behind it
 * in the order they are filled from.
 */
export function ProductCard({ product, editable = false, crop }: ProductCardProps) {
  const navigate = useNavigate();
  const price = toNum(product.website_price);
  return (
    <button
      type="button"
      disabled={!editable}
      onClick={() => navigate(`/nursery/products/${product.id}/edit`)}
      className="w-full text-left bg-white border border-neutral-200 rounded-xl p-4 hover:shadow-md transition-shadow disabled:cursor-default disabled:hover:shadow-none focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <p className="min-w-0 font-semibold text-neutral-800 text-sm leading-tight break-words flex items-center gap-1.5">
          <TagSwatch hex={product.tag_colour_hex} name={product.tag_colour_name} />
          <span className="min-w-0 break-words">
            {product.name}
            {(product.tree_type_name || product.tree_type_code) && (
              <span className="block text-xs font-normal text-neutral-500">{product.tree_type_name || product.tree_type_code}</span>
            )}
          </span>
        </p>
        {!product.active && (
          <span className="flex-shrink-0 text-xs px-2 py-0.5 rounded-full bg-red-50 text-red-500">Retired</span>
        )}
      </div>
      {crop && <p className="text-xs font-medium text-primary-700 mb-1">{crop}</p>}
      {product.tag_colour_name && (
        <p className="text-xs text-neutral-500 mb-1">{product.tag_colour_name} tag</p>
      )}
      <p className="text-xs font-medium text-neutral-500 uppercase tracking-wide mt-2 mb-1">Filled from</p>
      {product.members.length === 0 ? (
        <p className="text-xs text-amber-700">No stock items — this cannot be sold until it has one.</p>
      ) : (
        <ol className="space-y-0.5">
          {product.members.map((m, i) => (
            <li key={m.stock_item_id} className="text-xs text-neutral-700 break-words">
              <span className="text-neutral-400 mr-1">{i + 1}.</span>
              {[m.cultivar_name, m.rootstock_name].filter(Boolean).join(' / ')}
              {/* Only one saved before a product was held to one tree type. */}
              {m.tree_type_id != null && product.tree_type_id != null && m.tree_type_id !== product.tree_type_id && (
                <span className="text-red-500"> · {m.tree_type_name || m.tree_type_code}, remove</span>
              )}
              {!m.active && <span className="text-red-500"> · inactive</span>}
            </li>
          ))}
        </ol>
      )}
      <div className="mt-3 pt-3 border-t border-neutral-100 text-xs text-neutral-500">
        {price != null ? (
          <>Website: <span className="font-medium text-neutral-800">
            {price.toLocaleString('en-AU', { style: 'currency', currency: 'AUD' })}
          </span></>
        ) : (
          <span className="text-neutral-400">Not sold online</span>
        )}
        {product.sku_code && <span className="ml-2 text-neutral-400">SKU: {product.sku_code}</span>}
      </div>
    </button>
  );
}
